/** @jsxImportSource @emotion/react */
import { useEffect, useState } from "react";

import moment from "moment";

import { useTableStoreByRoute } from "@/stores/useTableStore";

import { DATE_FORMATS, Filter, ROUTES, useDateTime } from "../../shared";
import RectangleLoader from "../../shared/components/loaders/RectangleLoader";
import DayRangePicker from "../DatePickers/DayPickers/DayRangePicker/DayRangePicker";
import useFilterDropdown from "../Filters/hooks/useFilterDropdown";
import useFilteredValues from "../Filters/hooks/useFilteredValues";
import { FILTER_KEYS, FILTER_NAMES } from "../Filters/types";

interface DayRangeFilterProps {
  route: ROUTES;
  minDate: moment.Moment;
  setExternalPayload?: () => void;
}

const DayRangeFilter = ({
  route,
  minDate,
  setExternalPayload
}: DayRangeFilterProps) => {
  const { formattedDate } = useDateTime();

  const {
    params,
    loading: isTableLoading,
    initialLoading: isInitialLoading
  } = useTableStoreByRoute(route);

  const filterName = FILTER_NAMES.DateRange;

  const { handleFilterChangeValue } = useFilterDropdown(route, filterName);

  const { handleDeleteFilterValue, dateRangeFilterObject, filteredValues } =
    useFilteredValues();

  const filterObject = params.filters;

  const [filterValue, setFilterValue] = useState<Filter[]>([]);
  const [externalValue, setExternalValue] = useState<{
    start: Date;
    end: Date;
  } | null>(null);

  useEffect(() => {
    const stored = filterObject?.[filterName];

    if (stored && stored.length > 0) {
      setFilterValue(stored);
    } else {
      setFilterValue([]);
      setExternalValue(null);
    }
  }, [filterObject, filterName]);

  useEffect(() => {
    if (filterValue.length > 0 && filteredValues.length > 0) {
      setExternalValue({
        start: moment(filterValue[0].id).toDate(),
        end: moment(filterValue[1].id).toDate()
      });
    }
  }, [filterValue, filteredValues]);

  const handleChange = (value: { start: Date; end: Date } | null) => {
    if (!value) return;

    const { start, end } = value;

    const dateFromTo = [
      {
        id: formattedDate(moment(start), DATE_FORMATS.ISO_8601_no_z),
        name: FILTER_KEYS.DateRangeFrom
      },
      {
        id: formattedDate(moment(end), DATE_FORMATS.ISO_8601_no_z, true),
        name: FILTER_KEYS.DateRangeTo
      }
    ];

    setExternalValue({ start, end });

    setTimeout(() => {
      handleFilterChangeValue({
        isTableLoading,
        filterObject,
        newValue: dateFromTo as Filter[],
        filterName,
        setValue: setFilterValue
      });
    }, 50);
  };

  if (isInitialLoading && !setExternalPayload) {
    return (
      <RectangleLoader height={36} testId={`${filterName}-filter-loader`} />
    );
  }

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    const hasDateFilter = filteredValues.some(
      item => item.id === FILTER_KEYS.Date
    );

    if (hasDateFilter) handleDeleteFilterValue(dateRangeFilterObject);
    setFilterValue([]);
    setExternalValue(null);
  };

  return (
    <DayRangePicker
      minDate={minDate}
      maxDate={moment()}
      disabled={isTableLoading}
      externalValue={externalValue}
      onChange={handleChange}
      onClear={handleClear}
      smallerInput
    />
  );
};

export default DayRangeFilter;
